import React, { useState, useEffect } from 'react'

import { LazyLoadImage } from 'react-lazy-load-image-component'
import { ClockIcon, CalendarIcon, FastForwardIcon, ReceiptTaxIcon, CheckCircleIcon } from '@heroicons/react/solid'
import { formatInTimezone } from '@/utils/date'
import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import PrimaryButton from '@/shared/Buttons/Primary'
import DefaultButton from '@/shared/Buttons/Default'
import { Modal, useModal } from '@/shared/Modal'

const CohortRow = ({cohort, cohortInterest, updateCost, alreadyEnrolled, alreadyTaken, following}) => {
  const { getRequest, putpostRequest } = useQuery()
  const [, setToast] = useGlobalState('toast')
  const [events, setEvents] = useState([])
  const [currentUser] = useGlobalState('currentUser')
  const isFreeMember = ['lead', 'beta'].includes(currentUser.access)
  const hideSelectToggle = alreadyEnrolled || alreadyTaken
  const { openModal, closeModal, isOpen } = useModal()

  const [ selected, setSelected ] = useState(cohortInterest?.includes(cohort.id))
  const [ followed, setFollowed ] = useState(following?.includes(cohort.courseId))
  const [ loading, setLoading ] = useState(false)
  const soldOut = cohort.seatsRemaining <= 0

  const toggleFollow = () => {
    setLoading(true)
    putpostRequest(`/api/v1/courses/${cohort.courseId}/follow`, 'POST', {}, (err, jsonData) => {
      if (err) { 
        console.log('toast errors'); 
        setLoading(false);
        return;
      }

      const newValue = !followed
      setFollowed(newValue)
      setLoading(false)
    })
  }
  const toggleSelect = () => {
    setLoading(true)
    let data = {
      cohort_id: cohort.id
    }
    putpostRequest(`/api/v1/cohort_interests/select`, 'POST', data, (err, jsonData) => {
      if (err) { 
        console.log('toast errors'); 
        setLoading(false);
        return;
      }

      const newValue = !selected
      setSelected(newValue)
      if (newValue === true) {
        updateCost(cohort.creditCost)
      } else {
        updateCost(-cohort.creditCost)
      }
      setLoading(false)
    })
  }

  useEffect(() => {
    if (!isOpen) { return }

    fetchData()
  }, [isOpen])

  const fetchData = () => {
    if (events.length > 0) { return }
    const data = { cohort_id: cohort.id }
    getRequest(`/api/v1/events/class_sessions`, data, (err, jsonData) => {
      if (err) { /* handled in hook */ return }
      setEvents(jsonData.events)
    })
  }

  return <>
    <div className={`group ${selected ? 'bg-clcnavy bg-opacity-10' : 'bg-white dark:bg-gray-800 hover:bg-slate-50 dark:hover:bg-slate-800/80'} flex flex-col sm:flex-row items-center gap-4 p-4 sm:p-5 rounded-2xl shadow-md shadow-slate-900/[0.05] hover:shadow-xl hover:shadow-clcpurple/20 border border-slate-200 dark:border-slate-700 hover:border-clcpurple/40 hover:-translate-y-0.5 transition-all duration-300`}>
      <div className="min-w-0 flex-1 flex flex-col md:flex-row items-center justify-start flex-wrap sm:flex-nowrap">
        <a href={`/courses/${cohort.courseSlug}`} className='rounded-lg overflow-hidden hover:no-underline' >
          <div className='w-60 sm:w-48'>
            <div className="aspect-w-16 aspect-h-9 mt-2 sm:mt-0 overflow-hidden rounded-lg">
              <img className="object-cover shadow-lg rounded-lg transform transition-transform duration-500 group-hover:scale-105" src={`${cohort.imageUrl}?aspect=16:9&width=420`} alt={cohort.name} />
            </div>
          </div>
        </a>
        <div className="min-w-0 flex-auto px-2 sm:px-4 md:grid md:grid-cols-2 md:gap-4">
          <a href={`/courses/${cohort.courseSlug}`} className='p-2 rounded-lg hover:no-underline' >
            <div className="flex items-center mb-1">
              <span className="uppercase text-xs font-medium tracking-wider bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded mr-2">{cohort.moniker}</span>
              { selected && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:text-green-200 border border-green-200"><svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path></svg>Selected</span> }
            </div>
            <p className="font-semibold text-lg text-clcpurple dark:text-clcpurple-light truncate mb-2 group-hover:text-clcpurple-dark dark:group-hover:text-clcpurple-light transition-colors duration-200">{cohort.name}</p>
            <div className='mt-4 text-sm font-medium text-clcnavy dark:text-blue-400 transition-colors duration-200'>Full Course Details →</div>
          </a>
          <div className="text-gray-600 dark:text-gray-300 text-sm space-y-3">
            <span className='flex items-center'><CalendarIcon className='w-5 h-5 mr-2 text-clcnavy dark:text-blue-400' /><strong>{formatInTimezone(cohort.startsAtUtc, currentUser.timezone, 'EEE, MMM d, Y')} - {formatInTimezone(cohort.endsAtUtc, currentUser.timezone, 'EEE, MMM d, Y')}</strong></span>
            <span className='flex items-center'>
              <ClockIcon className='w-5 h-5 mr-2 text-clcnavy dark:text-blue-400' /> <div onClick={openModal} className='text-clcnavy hover:text-indigo-700 dark:hover:text-blue-300 underline cursor-pointer transition-colors duration-200'>See Detailed Schedule</div>
            </span>
            { !alreadyEnrolled && <span className='text-gray-600 dark:text-gray-300 text-sm flex items-center'><ReceiptTaxIcon className='w-5 h-5 mr-2 text-clcnavy dark:text-blue-400' /><span className="font-medium">${cohort.nonmemberPrice}</span> <span className="text-xs ml-1">(members ${cohort.price})</span></span> }
          </div>
        </div>
      </div>
      <div className='w-48 flex items-center justify-center'>
        { Boolean(hideSelectToggle) && <>
          <div className='flex-shrink-0 w-full flex flex-col items-center justify-center'>
            { alreadyTaken && <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:text-green-200 shadow-sm border border-green-200">You've taken this course</span> }
            { alreadyEnrolled && <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:text-green-200 shadow-sm border border-green-200">Enrolled</span> }
          </div>
        </> }
        { !Boolean(hideSelectToggle) && <>
          <div className='flex-shrink-0 w-full flex flex-col items-center space-y-3'>
            { !selected && !soldOut && 
              <PrimaryButton 
                className='w-full transition-all duration-200 shadow-md hover:shadow-lg' 
                onClick={toggleSelect} 
                disabled={loading || soldOut} 
                text={loading ? "Processing..." : `Select (${cohort.seatsRemaining} seats left)`} 
              /> 
            }
            { soldOut && <>
              <PrimaryButton onClick={toggleSelect} className='w-full opacity-80' disabled={true} text={`Sold Out`} />
              { !followed && 
                <PrimaryButton 
                  className='w-full transition-all duration-200' 
                  onClick={toggleFollow} 
                  disabled={loading}
                  text={loading ? "Setting reminder..." : 'Remind Me'} 
                /> 
              }
              { followed && 
                <DefaultButton 
                  className='w-full border-green-300 transition-all duration-200' 
                  onClick={toggleFollow} 
                  text={<span className='text-green-600 flex items-center justify-center'><svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>Reminder Set!</span>} 
                /> 
              }
            </>
            }
            { selected && 
              <DefaultButton 
                className='w-full transition-all duration-200' 
                onClick={toggleSelect} 
                disabled={loading}
                text={loading ? "Canceling..." : 'Cancel'} 
              /> 
            }
            { !soldOut && !selected && <>
              <div className="w-full border-t border-gray-200 dark:border-gray-700 my-1 py-1"></div>
              { !followed && 
                <DefaultButton 
                  className='w-full transition-all duration-200' 
                  onClick={toggleFollow} 
                  disabled={loading}
                  text={loading ? "Setting reminder..." : 'Remind Me'} 
                /> 
              }
              { followed && 
                <DefaultButton 
                  className='w-full border-green-300 transition-all duration-200' 
                  onClick={toggleFollow} 
                  text={<span className='text-green-600 flex items-center justify-center'><svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>Reminder Set!</span>} 
                /> 
              }
            </> }
          </div>
        </> }
      </div>
    </div>
    <Modal isOpen={isOpen} closeModal={closeModal} >
      <h2 className="text-lg font-semibold text-indigo-600 tracking-wide uppercase mb-4">Detailed Schedule</h2>
      <div className="max-w-7xl mx-auto py-4 px-4 sm:py-6 sm:px-6 lg:px-8 bg-gray-50 dark:bg-gray-800 rounded-lg">

        <div className="mb-4"><span className="font-medium text-gray-700 dark:text-gray-300">Cohort Starts At:</span><br /> {formatInTimezone(cohort.startsAtUtc, currentUser.timezone, 'EEE MMMM d, Y h:mmaaa zzz')}</div>
        {cohort.sessionDuration > 0 && (
          <div className="mb-4"><span className="font-medium text-gray-700 dark:text-gray-300">Each Session:</span><br /> {cohort.sessionDuration} minutes</div>
        )}
        <div className="mb-5"><span className="font-medium text-gray-700 dark:text-gray-300">Your Account Timezone:</span><br /> {currentUser.timezone} ({formatInTimezone(cohort.startsAtUtc, currentUser.timezone, 'zzz')}) <br /><span className='text-xs text-gray-500'>You can change your timezone in your settings</span></div>
        <ul className="border-l-2 mx-auto max-w-lg border-dashed border-indigo-400 mt-6 mb-6">
          {events.map((event, index) => <li key={`sch${event.id}`} className="flex flex-col px-4 py-3 last:pb-1 sm:px-8 ml-2 relative before:absolute before:w-3 before:h-3 before:bg-indigo-500 before:rounded-full before:-left-7 before:top-4">
            <h4 className="mb-2 text-gray-900 dark:text-gray-100 text-xl font-bold">{event.name}</h4>
            <p className="text-base mb-3 font-medium text-clcpurple">{formatInTimezone(event.startsAtUtc, currentUser.timezone, 'EEE MMMM d, Y h:mmaaa zzz')}</p>
          </li> )}
        </ul>
      </div>
      <button className='inline-flex items-center px-5 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 mt-4' onClick={closeModal}>
        <CheckCircleIcon className="w-4 h-4 mr-1.5" />
        Close
      </button>
    </Modal>
  </>
}

export default CohortRow
